import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { hasWindow } from "@omni/utils/dom.utils";
import { isDefined } from "@omni/utils/helper.utils";
import queryString from "query-string";
import { createContext, use, useEffect } from "react";
import { useSelector } from "react-redux";

import { CartItemModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import { getCart } from "@/redux/modules/api/data/cart/cart.selectors";

import { useProductDetail } from "../hooks/useProductDetail.hook";
import { useProductConfigurationContext } from "./productConfiguration.context";

type CartProductContextValue = {
    cartProduct: CartItemModel | undefined;
};

export type CartProductContextProviderProps = {};

export const CartProductContext = createContext<
    CartProductContextValue | undefined
>(undefined);

CartProductContext.displayName = "CartProductContext";

export const CartProductContextProvider = ({
    children
}: RequireChildrenFor<CartProductContextProviderProps>) => {
    const shoppingCart = useSelector(getCart);
    const { updateProductDetail } = useProductDetail();
    const { setConfiguration } = useProductConfigurationContext();

    const qpCartEntry = queryString.parse(!hasWindow() ? "" : location.search)
        ?.cartid as string;
    const cartProduct = shoppingCart?.items.find((i) => i.id === qpCartEntry);

    // biome-ignore lint/correctness/useExhaustiveDependencies: <TODO>
    useEffect(() => {
        if (
            !cartProduct?.configuration ||
            !Object.keys(cartProduct.configuration || {})
        ) {
            return;
        }

        updateProductDetail({
            quantity: cartProduct.quantity,
            deliveryType: cartProduct.deliveryType
        });
        setConfiguration(cartProduct.configuration);
    }, [cartProduct]);

    return (
        <CartProductContext value={{ cartProduct }}>
            {children}
        </CartProductContext>
    );
};

export const useCartProductContext = (): CartProductContextValue => {
    const context = use<CartProductContextValue | undefined>(
        CartProductContext
    );
    if (!isDefined(context)) {
        throw new Error(`${CartProductContext.displayName} is undefined.`);
    }
    return context;
};
